import { jwtDecode } from 'jwt-decode'

import { DEFAULT_MAIN_PAGE_SIZE, DEFAULT_RECOMMENDATION_SIZE } from '@/constants/general'
import { AvailableLocale } from '@/i18n/helpers'
import {
    APICategoryDataType,
    APICategorySeoInfo,
    APICategoryShortInfo,
    APIGameInfoDTO,
    APIGameRecommendation,
    APIGeneralInfo,
    CategoryDataType,
    CategoryType,
    FormSubmitData,
    GameRecommendationType,
    PageType,
    PlatformType,
    RewardCardType,
    RewardPurchasePayload,
    UserTasksStats,
} from '@/types'

import { FetchRequest, FetchResult, GamesAndCategoriesRequestParams } from './types'

export class ApiRequests {
    public request: FetchRequest

    constructor({ request }: { request: FetchRequest }) {
        this.request = request
    }

    public submitContactForm = (data: FormSubmitData) => {
        return this.request('/api/v1/email/report', {
            method: 'POST',
            cache: 'no-store',
            body: JSON.stringify(data),
        })
    }

    public getGamesByCategories = async ({
        categoriesIds,
        platform,
        pagination,
        locale,
        withDebugInfo,
        backendFlags,
    }: GamesAndCategoriesRequestParams): Promise<CategoryDataType> => {
        const path = '/api/v1/categories/games'
        const search = new URLSearchParams()
        search.set('pagination', JSON.stringify(pagination))
        search.set('platform', platform)
        search.set('locale', locale)

        if (backendFlags) {
            search.set('flags', backendFlags)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }

        if (categoriesIds) {
            search.set('categoriesIds', JSON.stringify(categoriesIds))
        }
        const { originalResponse, data } = await this.request<APICategoryDataType>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }

        const gamesByCategories: APICategoryDataType = data
        return {
            categories: gamesByCategories.categories.map((category) => ({
                ...category,
                games: category.games,
            })),
            pagination: gamesByCategories.pagination,
        }
    }

    public getMainPageGamesByCategories = async ({
        platform,
        locale,
        withDebugInfo,
        backendFlags,
    }: {
        platform: PlatformType
        locale: string
        withDebugInfo: boolean
        backendFlags: string | undefined
    }): Promise<CategoryDataType> => {
        const path = '/api/v1/categories/main-page'
        const search = new URLSearchParams()
        search.set('platform', platform)
        search.set('pageSize', DEFAULT_MAIN_PAGE_SIZE.toString())
        search.set('locale', locale)

        if (backendFlags) {
            search.set('flags', backendFlags)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }
        const { originalResponse, data } = await this.request<APICategoryDataType>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }

        const gamesByCategories: APICategoryDataType = data
        return {
            categories: gamesByCategories.categories.map((category) => ({
                ...category,
                games: category.games,
            })),
            pagination: gamesByCategories.pagination,
        }
    }

    public getMainCategories = async (locale: string): Promise<APIGeneralInfo> => {
        const path = '/api/v1/general/info'
        const search = new URLSearchParams()
        search.set('locale', locale)

        const { originalResponse, data } = await this.request<APIGeneralInfo>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }
        return data
    }

    public getGameByParam = async ({
        param,
        locale,
        backendFlags,
    }: {
        param: string
        locale: string
        backendFlags: string | undefined
    }): Promise<APIGameInfoDTO> => {
        const path = `/api/v1/games/info/${param}`
        const search = new URLSearchParams()
        search.set('locale', locale)
        if (backendFlags) {
            search.set('flags', backendFlags)
        }
        try {
            const { originalResponse, data } = await this.request<APIGameInfoDTO>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }
            const gameInfo: APIGameInfoDTO = data

            return gameInfo
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getRandomGameHRUs = async ({
        platform,
        amount,
    }: {
        platform: PlatformType
        amount?: number
    }): Promise<string[]> => {
        const path = '/api/v1/games/random-hrus'
        const search = new URLSearchParams()
        search.set('platform', platform)
        if (amount) {
            search.set('amount', amount.toString())
        }

        try {
            const { originalResponse, data } = await this.request<string[]>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getGamesCategoriesShortInfo = async (
        type: CategoryType,
        locale: string,
    ): Promise<APICategoryShortInfo[]> => {
        const path = `/api/v1/categories/short-info/${type}?locale=${locale}`
        try {
            const { originalResponse, data } = await this.request<APICategoryShortInfo[]>(path)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }
            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getGamesCategoriesSeoById = async ({
        id,
        locale,
        type,
        backendFlags,
    }: {
        id: string
        locale: AvailableLocale
        type: PageType
        backendFlags: string | undefined
    }): Promise<APICategorySeoInfo> => {
        const path = `/api/v1/categories/seo/${id}`
        const search = new URLSearchParams()
        search.set('locale', locale)
        search.set('type', type)
        if (backendFlags) {
            search.set('flags', backendFlags)
        }
        try {
            const { originalResponse, data } = await this.request<APICategorySeoInfo>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }
            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    // Decoder for signed jwt requests: decodes jwt body and attaches original data
    public signedRequest = async <T>(url: string, config: RequestInit = {}) => {
        const result = await this.request<string>(url, config)
        if (!result.data) {
            return result as FetchResult<T>
        }

        const { data: jwt } = result

        return {
            ...result,
            data: {
                jwt,
                ...jwtDecode<T>(jwt),
            },
        }
    }

    public getGameRecommendations = async <T extends GameRecommendationType[]>({
        hru,
        types,
        platform,
        locale,
        limit = DEFAULT_RECOMMENDATION_SIZE,
    }: {
        hru: string
        types: T
        platform: PlatformType
        locale: string
        limit?: number
    }): Promise<APIGameRecommendation<T>> => {
        const search = new URLSearchParams()
        search.set('rec_types', types.join(','))
        search.set('limit', String(limit))
        search.set('platform', platform)
        search.set('locale', locale)

        const URL = `/api/v1/games/recommendations/${hru}?${search}`
        try {
            const { originalResponse, data } = await this.request<APIGameRecommendation<T>>(URL)
            if (!originalResponse.ok || !data) {
                // maybe log the reason - it's not clear
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public purchaseReward = async ({ rewardId, userEmail, countryCode }: RewardPurchasePayload): Promise<void> => {
        const URL = '/api/v1/rewards/purchase'
        try {
            const { originalResponse } = await this.request(URL, {
                method: 'post',
                body: JSON.stringify({
                    reward_id: rewardId,
                    user_email: userEmail,
                    country_code: countryCode,
                }),
            })
            if (!originalResponse.ok) {
                return Promise.reject(originalResponse.status)
            }
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getRewards = async (): Promise<RewardCardType[]> => {
        const URL = '/api/v1/rewards'
        try {
            const { originalResponse, data } = await this.request<RewardCardType[]>(URL)

            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public claimTask = async (taskId: string): Promise<UserTasksStats> => {
        const URL = '/api/v1/p2e/claim'
        try {
            const { originalResponse, data } = await this.request<UserTasksStats>(URL, {
                method: 'post',
                body: JSON.stringify({
                    task_id: taskId,
                }),
            })
            if (!originalResponse.ok || !data) {
                // maybe log the reason - it's not clear
                return Promise.reject(originalResponse.status)
            }
            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getTasks = async (): Promise<UserTasksStats> => {
        const URL = '/api/v1/p2e/current'

        try {
            const { originalResponse, data } = await this.request<UserTasksStats>(URL)

            if (!originalResponse.ok || !data) {
                // maybe log the reason - it's not clear
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }
}
